<script setup lang="ts">
import MemoryUsage from "@blackfisk/vue/components/MemoryUsage.vue";
import { useAppDrawer } from "@blackfisk/vue/composables/useAppDrawer";
import { usePerformanceProfiling } from "@blackfisk/vue/composables/usePerformanceProfiling";
import { useScanListener } from "@blackfisk/vue/composables/useScanListener";
import { useScanner } from "@blackfisk/vue/composables/useScanner";
import { AkMoreVerticalFill, MdBarcodeReader, UnWeight } from "@kalimahapps/vue-icons";
import { onKeyStroke, useMediaQuery } from "@vueuse/core";
import DynamicDialog from "primevue/dynamicdialog";
import Toast from "primevue/toast";
import { computed, onBeforeMount, ref, watch } from "vue";
import { RouterView, useRoute } from "vue-router";

import AppDrawerRailItems from "@/components/AppDrawerRailItems.vue";
import AppSettingsButton from "@/components/AppSettingsButton.vue";
import MainNavigation from "@/components/MainNavigation.vue";
import ReloadPrompt from "@/components/ReloadPrompt.vue";
import ThemeSwitch from "@/components/ThemeSwitch.vue";
import { useAccount } from "@/composables/useAccount";
import { useLogRocket } from "@/composables/useLogRocket";
import { usePrinters } from "@/composables/usePrinters";
import { useQuickLogin } from "@/composables/useQuickLogin";
import { useScaleWeight } from "@/composables/useScaleWeight";
import { useSentry } from "@/composables/useSentry";
import { useIdentityStore } from "@/stores/identity";

const { accountVersion } = useAccount();
const { registerScanner } = useScanner();
const { registerListeners, listeners } = useScanListener();
const {
  isReadingScale,
  updateScale,
  requestScale,
  scaleDevice,
  scaleUnits,
  scaleWeight,
  isScalePaired,
  isScaleOpened,
} = useScaleWeight();
onBeforeMount(() => {
  updateScale();
});
const connectScale = (): void => {
  if (!isScalePaired.value && !isScaleOpened.value) {
    requestScale();
  }
  if (isScalePaired.value && !isScaleOpened.value) {
    updateScale();
  }
};
const scaleStatusMessage = computed(() => {
  if (isScalePaired.value) {
    if (isReadingScale.value) {
      return `Scale: ${scaleDevice.value?.productName ?? "Generic Scale"} | ${scaleWeight.value} ${scaleUnits.value}`;
    }
    return `Scale: ${scaleDevice.value?.productName ?? "Generic Scale"}`;
  }
  if (!isScalePaired.value && !isScaleOpened.value) {
    return "Click to Connect a Scale";
  }
  return "Scale Status Unknown";
});
const {
  showContextDrawer,
  railPinned,
  toggleRailPinned,
  mobilePanelOpen,
  toggleMobilePanel,
  closeMobilePanel,
  viewTitle,
} = useAppDrawer();

const railHovered = ref(false);
const railFocusWithin = ref(false);
const railHoverSuppressed = ref(false);
// Must match tailwind.config.js screens.sm (980px) — the vertical-rail breakpoint.
const isVerticalRail = useMediaQuery("(min-width: 980px)");
// Hover-peek only makes sense with a real pointer.
const canHover = useMediaQuery("(hover: hover)");
const railExpanded = computed(
  () =>
    mobilePanelOpen.value ||
    (isVerticalRail.value &&
      (railPinned.value ||
        (canHover.value &&
          !railHoverSuppressed.value &&
          (railHovered.value || railFocusWithin.value)))),
);
const onRailMouseEnter = (): void => {
  railHovered.value = true;
};
const onRailMouseLeave = (): void => {
  railHovered.value = false;
  railHoverSuppressed.value = false;
};
const onRailFocusIn = (): void => {
  railFocusWithin.value = true;
};
const onRailFocusOut = (event: FocusEvent): void => {
  const currentTarget = event.currentTarget as HTMLElement | null;
  if (!currentTarget?.contains(event.relatedTarget as Node | null)) {
    railFocusWithin.value = false;
  }
};
const onToggleRailExpanded = (): void => {
  if (railExpanded.value) {
    if (railPinned.value) {
      toggleRailPinned();
    }
    // Collapsing must beat hover/focus, otherwise the pointer still inside the
    // rail (or the just-clicked button's focus) would immediately re-expand it.
    railHoverSuppressed.value = true;
    railFocusWithin.value = false;
  } else if (!railPinned.value) {
    toggleRailPinned();
  }
};

const route = useRoute();
watch(
  () => route.fullPath,
  () => {
    closeMobilePanel();
  },
);
watch(isVerticalRail, (vertical) => {
  if (vertical) {
    closeMobilePanel();
  }
});
onKeyStroke("Escape", () => {
  closeMobilePanel();
});

useQuickLogin();

registerScanner();
registerListeners();

useLogRocket();

const { initializeSentryUser } = useSentry();
initializeSentryUser();

const IdentityStore = useIdentityStore();

const { refreshPrintersAndPaper } = usePrinters();
if (IdentityStore.isAuthenticated) {
  refreshPrintersAndPaper();
}

const environment = import.meta.env.VITE_ENVIRONMENT;
const { isMemoryUsageDisplayEnabled } = usePerformanceProfiling();
</script>

<template>
  <div class="bf-background-dark bf-text flex min-h-screen flex-col">
    <DynamicDialog />
    <ReloadPrompt />
    <div class="flex flex-1 flex-col">
      <header class="w-full bg-surface-50 pt-[env(safe-area-inset-top)] dark:bg-surface-900">
        <MainNavigation />
      </header>
      <Toast
        position="bottom-right"
        group="br"
        class="!z-[1200]"
        :pt="{ root: { style: { bottom: 'calc(20px + env(safe-area-inset-bottom, 0px))' } } }"
      />
      <Toast
        position="bottom-center"
        group="bc"
        class="!z-[1200]"
        :pt="{ root: { style: { bottom: 'calc(20px + env(safe-area-inset-bottom, 0px))' } } }"
      />
      <MemoryUsage v-if="environment === 'local' && isMemoryUsageDisplayEnabled" />
      <div class="flex w-full flex-1 flex-col sm:flex-row">
        <div
          v-if="IdentityStore.isAuthenticated"
          class="relative min-h-[4.5rem] sm:min-h-0 sm:w-14 sm:shrink-0"
        >
          <div
            class="group/rail absolute inset-x-0 top-0 z-20 flex min-h-[4.5rem] w-full flex-row items-start justify-start gap-4 border-r border-r-surface-100 bg-surface-50 px-2.5 py-4 pt-6 shadow-sm transition-colors dark:border-r-surface-750 dark:bg-surface-800 sm:inset-y-0 sm:left-0 sm:right-auto sm:z-30 sm:min-h-0 sm:flex-col sm:items-stretch sm:overflow-hidden sm:transition-[width] sm:duration-200"
            :class="[railExpanded ? 'sm:w-56' : 'sm:w-14', mobilePanelOpen ? 'z-40' : '']"
            :data-expanded="railExpanded ? 'true' : 'false'"
            :data-panel="mobilePanelOpen ? 'open' : 'closed'"
            @mouseenter="onRailMouseEnter"
            @mouseleave="onRailMouseLeave"
            @focusin="onRailFocusIn"
            @focusout="onRailFocusOut"
          >
            <!-- * App Drawer Subscribers -->
            <div
              id="app-drawer-mobile-panel"
              class="grid flex-1 grid-flow-col auto-cols-fr items-start gap-2 sm:flex sm:flex-none sm:flex-col sm:items-stretch sm:gap-4"
            >
              <AppDrawerRailItems />
              <!-- * Scale -->
              <div class="hidden items-center gap-2 sm:flex" v-tooltip.left="scaleStatusMessage">
                <h1
                  class="text-2xl"
                  :class="{
                    'cursor-pointer': !isScalePaired && !isScaleOpened,
                    'text-primary-500/70': isScalePaired,
                    'text-surface-300 dark:text-surface-500': !isScalePaired,
                  }"
                  @click.prevent="connectScale"
                >
                  <UnWeight />
                </h1>
                <span
                  class="hidden text-sm whitespace-nowrap group-data-[expanded=true]/rail:inline"
                >
                  Scale
                </span>
              </div>
              <!-- * Scanner -->
              <div
                v-if="listeners.length > 0"
                v-tooltip.left="
                  `Scan: ${Array.from(new Set(listeners.map((listener) => Array.from(new Set(Object.keys(listener.bindings))).join(',')))).join(',')}`
                "
                class="hidden items-center gap-2 sm:flex"
              >
                <h1 class="text-2xl text-primary-500/70">
                  <span class="inline-block scale-x-[-1]"><MdBarcodeReader /></span>
                </h1>
                <span
                  class="hidden text-sm whitespace-nowrap group-data-[expanded=true]/rail:inline"
                >
                  Scanner
                </span>
              </div>
            </div>
            <div
              class="ml-auto flex flex-row items-start gap-6 sm:mt-auto sm:ml-0 sm:flex-col sm:items-stretch sm:gap-4"
            >
              <button
                type="button"
                class="hidden cursor-pointer text-2xl text-surface-300/50 hover:text-primary-500 sm:block"
                :aria-pressed="railExpanded"
                :aria-label="railExpanded ? 'Collapse sidebar' : 'Expand sidebar'"
                @click="onToggleRailExpanded"
              >
                <AkMoreVerticalFill />
              </button>
              <ThemeSwitch class="flex w-min sm:hidden" />
              <div class="flex flex-col items-center gap-1 sm:flex-row sm:items-center sm:gap-2">
                <AppSettingsButton class="flex h-min w-fit" />
                <span
                  class="hidden text-sm whitespace-normal sm:whitespace-nowrap sm:group-data-[expanded=true]/rail:inline"
                >
                  Settings
                </span>
              </div>
              <button
                type="button"
                class="flex cursor-pointer text-2xl text-surface-300/50 hover:text-primary-500 sm:hidden"
                :aria-expanded="mobilePanelOpen"
                aria-controls="app-drawer-mobile-panel"
                :aria-label="
                  mobilePanelOpen
                    ? `Collapse ${viewTitle ?? 'navigation'}`
                    : `Expand ${viewTitle ?? 'navigation'}`
                "
                @click="toggleMobilePanel"
              >
                <AkMoreVerticalFill />
              </button>
            </div>
          </div>
          <div
            v-if="mobilePanelOpen"
            class="fixed inset-0 z-30 bg-surface-900/20 sm:hidden"
            @click="closeMobilePanel"
          />
        </div>
        <div
          v-show="showContextDrawer"
          id="context-drawer"
          class="flex w-full min-w-fit md:max-w-lg"
        />
        <div class="flex w-full flex-col pb-[env(safe-area-inset-bottom)]">
          <div id="pre-content-teleport" />
          <RouterView :key="accountVersion" />
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="postcss" scoped></style>
